import { useState } from 'react';
import { useApp } from '../context/useApp';
import { useLanguage } from '../i18n';
import { buildInterviewBrief } from '../prompts/interviewBrief';
import { X, ArrowRight } from 'lucide-react';

interface LegacyMigrationPromptProps {
  onClose: () => void;
}

export default function LegacyMigrationPrompt({ onClose }: LegacyMigrationPromptProps) {
  const { t } = useLanguage();
  const i = t.interview;
  const { projects, legacyMigrationPending, markLegacyMigrationDone, setActiveProjectId } = useApp();
  const [running, setRunning] = useState(false);

  const candidates = projects.filter((p) => legacyMigrationPending.includes(p.id));

  if (candidates.length === 0) {
    markLegacyMigrationDone();
    return null;
  }

  const openOne = async (projectId: string) => {
    setRunning(true);
    try {
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;
      const sectionBlob = Object.entries(project.sections || {})
        .map(([k, v]: any) => `## ${k}\n\n${v?.content || ''}`)
        .join('\n\n');
      const seed = {
        name: project.name,
        idea: project.input?.idea || '',
        industry: project.input?.industry,
        location: project.input?.location,
        teamSize: project.input?.teamSize,
        stage: project.input?.stage,
        goals: project.input?.goals,
        startingMaterial: sectionBlob,
      };
      const brief = buildInterviewBrief({
        kind: 'project',
        seed,
        locale: project.input?.locale || 'en',
      });
      const response = await fetch('/api/interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          op: 'start',
          payload: { kind: 'project', seed, projectId, locale: seed.industry ? 'en' : 'en', brief },
        }),
      });
      const data = await response.json();
      if (!response.ok) return;
      if (data.session?.id) {
        setActiveProjectId(projectId);
        onClose();
        window.location.assign(`/app?session=${data.session.id}`);
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-stretch sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white shadow-2xl w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-auto border-0 sm:border border-cloud flex flex-col">
        <div className="p-4 sm:p-6 border-b border-cloud flex items-center justify-between sticky top-0 bg-white z-10 safe-top">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-lg sm:text-xl font-bold truncate">{i.migrationTitle}</h2>
            <p className="text-slate text-sm mt-1">{i.migrationSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              markLegacyMigrationDone();
              onClose();
            }}
            className="p-2 -mr-1 text-ash hover:text-ink hover:bg-mist min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <ul className="p-3 sm:p-4 divide-y divide-mist flex-1">
          {candidates.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="font-medium text-ink truncate">{p.name}</div>
                <div className="text-xs text-slate truncate">{p.input?.idea || '—'}</div>
              </div>
              <button
                type="button"
                disabled={running}
                onClick={() => openOne(p.id)}
                className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-100 flex items-center gap-1.5 transition-colors disabled:opacity-50 shrink-0"
              >
                {i.migrationRunOne} <ArrowRight size={14} />
              </button>
            </li>
          ))}
        </ul>
        <div className="p-4 border-t border-cloud flex justify-end gap-2 safe-bottom">
          <button
            type="button"
            onClick={() => {
              markLegacyMigrationDone();
              onClose();
            }}
            className="min-h-[44px] px-4 py-2 text-ink hover:bg-mist font-medium"
          >
            {i.migrationSkip}
          </button>
        </div>
      </div>
    </div>
  );
}
